import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  ButtonBase,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import { useSnackbar } from 'notistack';
import api from '../services/api';
import { TenantInfo } from '../types';
import { resolveTenantAccent, tenantAccentMap } from '../utils/tenantAccentColor';
import { buildHandoffUrl } from '../utils/sessionAuthStore';
import { getApiErrorMessage } from '../utils/apiError';
import { useTenantAppearance } from '../contexts/TenantAppearanceContext';
import { accentInk } from '../utils/tenantAppearance';

export interface TenantSwitcherProps {
  tenants: TenantInfo[];
  tenantId: number | null;
  tenantName: string | null;
  tenantCode: string | null;
  /** compact = mobile centered chip-like; full = desktop switcher */
  variant?: 'full' | 'compact';
  onSwitch: (tenantId: number) => Promise<void>;
}

const TenantSwitcher: React.FC<TenantSwitcherProps> = ({
  tenants,
  tenantId,
  tenantName,
  tenantCode,
  variant = 'full',
  onSwitch,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [filter, setFilter] = useState('');
  const [switchingId, setSwitchingId] = useState<number | null>(null);
  const [overlayName, setOverlayName] = useState<string | null>(null);

  const { appearance, configuredAccents } = useTenantAppearance();
  const configured = useMemo(
    () =>
      tenantId != null && appearance.colorAcento
        ? { ...configuredAccents, [tenantId]: appearance.colorAcento }
        : configuredAccents,
    [tenantId, appearance.colorAcento, configuredAccents]
  );
  const accentMap = useMemo(
    () => ({ ...tenantAccentMap(tenants), ...configured }),
    [tenants, configured]
  );
  const rawAccent = resolveTenantAccent(tenantId, tenants, configured);
  const accent = rawAccent ? accentInk(rawAccent) : null;
  const open = Boolean(anchorEl);
  const showSearch = tenants.length > 5;

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return tenants;
    return tenants.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.code.toLowerCase().includes(q) ||
        String(t.id).includes(q)
    );
  }, [tenants, filter]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key.toLowerCase() === 'e') {
        event.preventDefault();
        const trigger = document.getElementById('tenant-switcher-trigger');
        if (trigger) {
          setAnchorEl(trigger);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setFilter('');
  };

  const handleSwitch = async (t: TenantInfo) => {
    if (t.id === tenantId || switchingId != null) return;
    setSwitchingId(t.id);
    setOverlayName(t.name);
    handleClose();
    try {
      await onSwitch(t.id);
    } catch (err) {
      setOverlayName(null);
      setSwitchingId(null);
      enqueueSnackbar(getApiErrorMessage(err, 'No se pudo cambiar de empresa'), { variant: 'error' });
    }
  };

  const handleOpenInNewTab = async (t: TenantInfo, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const response = await api.post<{ handoff: string }>('/api/auth/tabs', { tenantId: t.id });
      const url = buildHandoffUrl(response.data.handoff, window.location.pathname + window.location.search);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'No se pudo abrir la empresa en otra solapa'), { variant: 'error' });
    }
  };

  if (!tenantName) {
    return null;
  }

  const canSwitch = tenants.length > 1;

  return (
    <>
      {overlayName && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: (theme) => theme.zIndex.modal + 2,
            bgcolor: 'rgba(255,255,255,0.72)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          <CircularProgress size={36} />
          <Typography variant="h6" fontWeight={700}>
            Cambiaste a {overlayName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Cargando empresa…
          </Typography>
        </Box>
      )}

      <Tooltip
        title={
          canSwitch
            ? `${tenantName}${tenantCode ? ` (${tenantCode})` : ''} — Ctrl+Shift+E`
            : tenantName
        }
      >
        <ButtonBase
          id="tenant-switcher-trigger"
          onClick={canSwitch ? handleOpen : undefined}
          disabled={!canSwitch}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            minWidth: 0,
            maxWidth: variant === 'full' ? 240 : '100%',
            width: variant === 'compact' ? '100%' : 'auto',
            height: variant === 'full' ? 36 : 'auto',
            px: 1.5,
            py: variant === 'full' ? 0 : 0.5,
            borderRadius: 999,
            border: '1px solid',
            borderColor: accent ? alpha(accent, 0.28) : alpha('#00548f', 0.18),
            bgcolor: accent ? alpha(accent, 0.08) : alpha('#00548f', 0.04),
            color: accent || 'primary.main',
            textAlign: 'left',
            justifyContent: 'flex-start',
            flexShrink: 1,
            cursor: canSwitch ? 'pointer' : 'default',
            transition: 'background-color 0.15s ease, border-color 0.15s ease',
            '&:hover': canSwitch
              ? {
                  bgcolor: accent ? alpha(accent, 0.14) : alpha('#00548f', 0.08),
                  borderColor: accent ? alpha(accent, 0.45) : alpha('#00548f', 0.35),
                }
              : undefined,
          }}
        >
          <Box
            component="span"
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: accent || 'primary.main',
              flexShrink: 0,
            }}
          />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              noWrap
              sx={{ fontWeight: 400, fontSize: variant === 'full' ? '0.875rem' : '0.8rem', lineHeight: 1.2 }}
            >
              {tenantName}
            </Typography>
          </Box>
          {canSwitch && <ExpandMoreIcon sx={{ fontSize: 18, flexShrink: 0, opacity: 0.8 }} />}
        </ButtonBase>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{
          paper: {
            sx: { width: 360, maxWidth: '92vw', mt: 1, borderRadius: 2 },
          },
        }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Cambiar empresa
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Esta pestaña · o abrí otra en paralelo
          </Typography>
        </Box>
        {showSearch && (
          <Box sx={{ px: 2, pb: 1 }}>
            <TextField
              size="small"
              fullWidth
              autoFocus
              placeholder="Buscar empresa…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}
        <Divider />
        <Box sx={{ maxHeight: 360, overflow: 'auto', py: 0.5 }}>
          {filtered.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
              Sin resultados
            </Typography>
          )}
          {filtered.map((t) => {
            const itemAccent = accentInk(
              accentMap[t.id] || resolveTenantAccent(t.id, tenants, configured) || '#00548f'
            );
            const selected = t.id === tenantId;
            const busy = switchingId === t.id;
            return (
              <MenuItem
                key={t.id}
                selected={selected}
                disabled={busy || switchingId != null}
                onClick={() => handleSwitch(t)}
                sx={{ py: 1.25, alignItems: 'flex-start', gap: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}>
                  {selected ? (
                    <CheckIcon fontSize="small" sx={{ color: itemAccent }} />
                  ) : (
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: itemAccent,
                        ml: 0.5,
                      }}
                    />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={t.name}
                  secondary={t.code}
                  primaryTypographyProps={{ fontWeight: selected ? 700 : 500 }}
                  sx={{ flex: 1, minWidth: 0 }}
                />
                <Tooltip title="Abrir en pestaña nueva">
                  <IconButton
                    size="small"
                    aria-label={`Abrir ${t.name} en pestaña nueva`}
                    onClick={(e) => handleOpenInNewTab(t, e)}
                    disabled={switchingId != null}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </MenuItem>
            );
          })}
        </Box>
      </Popover>
    </>
  );
};

export default TenantSwitcher;
